import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";
import {
  Box,
  Button,
  Container,
  Divider,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  Typography,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { useStores } from "../../hooks/use-stores";
import { ContentPageStyles } from "./styles";

const BANNER_JPG = "/images/banner_vaulted.jpg";
const VAULTED_LOGO = "/images/Vaulted-Blue.png";

const useStyles = makeStyles((theme) => ({
  ...ContentPageStyles(theme),
  root: {
    backgroundColor: "#000",
    color: "#fff",
    width: "100%",
    minHeight: "100%",
    paddingBottom: theme.spacing(6),
  },
  card: {
    backgroundColor: "#fff",
    color: "#111",
    borderRadius: 6,
    overflow: "hidden",
    boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
  },
  topPad: {
    paddingTop: theme.spacing(2),
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(2, 2, 1.5),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  logo: {
    height: 64,
    width: "auto",
    display: "block",
    [theme.breakpoints.down("xs")]: {
      height: 56,
    },
  },
  meta: {
    fontSize: 14,
    lineHeight: 1.35,
    color: "#111",
    "& b": { fontWeight: 700 },
  },
  bannerWrap: {
    backgroundColor: "#fff",
  },
  banner: {
    width: "100%",
    height: "auto",
    display: "block",
  },
  body: {
    padding: theme.spacing(2.5, 2, 0),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2, 1.5, 0),
    },
  },
  title: {
    fontWeight: 800,
    fontSize: "1.35rem",
    marginBottom: theme.spacing(1),
  },
  paragraph: {
    color: "#222",
    lineHeight: 1.55,
    marginBottom: theme.spacing(1.5),
  },
  benefitsTitle: {
    fontWeight: 800,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  benefitsList: {
    paddingTop: 0,
    paddingBottom: 0,
    listStyleType: "disc",
    paddingLeft: theme.spacing(3),
  },
  listItem: {
    display: "list-item",
    paddingTop: 2,
    paddingBottom: 2,
    paddingLeft: 0,
  },
  signUpWrap: {
    padding: theme.spacing(2.5, 2, 3),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2, 1.5, 2.5),
    },
  },
  signUpButton: {
    backgroundColor: "#4CAF50",
    color: "#fff",
    fontWeight: 700,
    padding: theme.spacing(1.2, 2.5),
    "&:hover": {
      backgroundColor: "#43A047",
    },
  },
  finePrint: {
    marginTop: theme.spacing(1.5),
    fontSize: 12,
    color: "#444",
    lineHeight: 1.45,
  },
}));

function compactDateForVaulted(value) {
  if (!value) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    return trimmed;
  }
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = String(d.getFullYear());
    return `${mm}/${dd}/${yyyy}`;
  } catch {
    return null;
  }
}

function buildVaultedSignupUrl(currentUser) {
  const address = currentUser?.address || {};
  const params = {
    affiliatedCompany: "LIFE",
    email: currentUser?.email,
    firstName: currentUser?.firstName,
    lastName: currentUser?.lastName,
    phoneNumber:
      currentUser?.phoneNumber ??
      currentUser?.phone ??
      currentUser?.mobilePhone ??
      currentUser?.cellPhone,
    birthday: compactDateForVaulted(
      currentUser?.birthday ??
        currentUser?.birthDate ??
        currentUser?.dateOfBirth ??
        address?.birthday,
    ),
    addressLine1: address?.address1 ?? address?.line1 ?? address?.addressLine1,
    addressLine2: address?.address2 ?? address?.line2 ?? address?.addressLine2,
    city: address?.city,
    state: address?.region ?? address?.state,
    zip: address?.zip ?? address?.postalCode,
    customerId: currentUser?.userID ?? currentUser?.customerId,
  };

  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === null || v === undefined) return;
    const s = String(v).trim();
    if (!s) return;
    qs.set(k, s);
  });
  return `https://vaulted.com/signup?${qs.toString()}`;
}

function OrderGold() {
  const classes = useStyles();
  const { t } = useTranslation("starfish");
  const { authStore } = useStores();
  const { currentUser } = authStore;

  const signupUrl = useMemo(() => buildVaultedSignupUrl(currentUser), [
    currentUser,
  ]);

  return (
    <div className={classes.root}>
      <Container maxWidth="md" className={classes.topPad}>
        <div className={classes.card}>
          <div className={classes.headerRow}>
            <img src={VAULTED_LOGO} alt="Vaulted" className={classes.logo} />
            <Typography className={classes.meta} component="div">
              <div>
                <b>{t("orderGold.priceLabel")}:</b> {t("orderGold.priceValue")}
              </div>
              <div>
                <b>{t("orderGold.pvLabel")}:</b> {t("orderGold.pvValue")}
              </div>
            </Typography>
          </div>

          <div className={classes.bannerWrap}>
            <img
              src={BANNER_JPG}
              alt=""
              className={classes.banner}
              decoding="async"
            />
          </div>

          <div className={classes.body}>
            <Typography className={classes.title} component="h2">
              {t("orderGold.title")}
            </Typography>

            <Typography className={classes.paragraph} component="p">
              {t("orderGold.body")}
            </Typography>

            <Typography className={classes.benefitsTitle} component="h3">
              {t("orderGold.benefitsTitle")}
            </Typography>

            <List dense className={classes.benefitsList} component="ul">
              {[
                t("orderGold.benefit1"),
                t("orderGold.benefit2"),
                t("orderGold.benefit3"),
                t("orderGold.benefit4"),
                t("orderGold.benefit5"),
                t("orderGold.benefit6"),
                t("orderGold.benefit7"),
                t("orderGold.benefit8"),
              ].map((text) => (
                <ListItem key={text} className={classes.listItem} component="li">
                  <ListItemText primary={text} />
                </ListItem>
              ))}
            </List>

            <Box mt={2}>
              <Typography className={classes.paragraph} component="p">
                {t("orderGold.closing1")}
              </Typography>
              <Typography className={classes.paragraph} component="p">
                <b>{t("orderGold.closing2")}</b>
              </Typography>
            </Box>
          </div>

          <Divider />

          <div className={classes.signUpWrap}>
            <Box display="flex" justifyContent="center">
              <Button
                className={classes.signUpButton}
                variant="contained"
                size="large"
                onClick={() => window.open(signupUrl, "_blank", "noopener")}
              >
                {t("orderGold.signUp")}
              </Button>
            </Box>

            <Typography className={classes.finePrint} align="center" component="div">
              <div>
                *{t("orderGold.finePrintLine1Prefix")}{" "}
                <a
                  href="https://www.vaulted.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#d32f2f", textDecoration: "none" }}
                >
                  {t("orderGold.finePrintDomain")}
                </a>
                {t("orderGold.finePrintLine1Suffix")}
              </div>
              <div>
                **{t("orderGold.finePrintLine2")}
              </div>
            </Typography>
          </div>
        </div>
      </Container>
    </div>
  );
}

export default observer(OrderGold);

